import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  ResourceLayout,
  BottomDrawer,
  FormField,
  TextInput,
  Select,
  NumberInput,
  Alert,
  Badge,
  Paper,
  Text,
  type ResourceQuery,
} from '@vipso/shared';
import {
  useListInventoryTransfersQuery,
  useAddUpdateInventoryTransferMutation,
  useConfirmInventoryTransferMutation,
  useDeleteInventoryTransferMutation,
  useListProductsQuery,
  useListRacksQuery,
} from '../api/tenantApi';
import { useListWarehousesQuery } from '../api/enhanceGenerated';
import { useModuleAccess } from '../hooks/useModuleAccess';
import type { InventoryTransferRow } from '../api/tenantApi';

const FROM_TYPE_OPTIONS = [
  { value: 'PURCHASE_POOL', label: 'Purchase Pool' },
  { value: 'RACK', label: 'Rack' },
];

const TO_TYPE_OPTIONS = [
  { value: 'RACK', label: 'Rack' },
  { value: 'PURCHASE_POOL', label: 'Purchase Pool' },
];

const defaultForm = {
  productId: '',
  fromType: 'PURCHASE_POOL' as 'PURCHASE_POOL' | 'RACK',
  fromRackId: '',
  toType: 'RACK' as 'RACK' | 'PURCHASE_POOL',
  toRackId: '',
  warehouseId: '',
  quantity: 0,
  lossPercent: 0,
  lossKg: 0,
  notes: '',
};

/** Prefer explicit lossKg; else percent of moved qty. */
function resolveLossKg(quantity: number, lossPercent: number, lossKg: number): number {
  if (lossKg > 0) return lossKg;
  if (lossPercent > 0) return (quantity * lossPercent) / 100;
  return 0;
}

export function InventoryTransferPage() {
  const { t } = useTranslation();
  const access = useModuleAccess('inventory');
  const canOpenRow = access.canUpdate || access.canConfirm || access.canDelete;

  const [query, setQuery] = useState<ResourceQuery>({ page: 1, pageLimit: 20, search: '' });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<InventoryTransferRow | null>(null);
  const [form, setForm] = useState(defaultForm);

  const { data, isLoading, isFetching, isError } = useListInventoryTransfersQuery({
    page: query.page,
    pageLimit: query.pageLimit,
  });

  const { data: productsData } = useListProductsQuery({ pageLimit: 200 });
  const { data: racksData } = useListRacksQuery({ pageLimit: 200 });
  const { data: warehousesData } = useListWarehousesQuery({ pageLimit: 100 });

  const [save, { isLoading: saving }] = useAddUpdateInventoryTransferMutation();
  const [confirm, { isLoading: confirming }] = useConfirmInventoryTransferMutation();
  const [remove] = useDeleteInventoryTransferMutation();

  const list = data?.listInventoryTransfers;

  const productOptions = (productsData?.listProducts?.rows ?? []).map((p) => ({
    value: p.id,
    label: p.name,
  }));

  const rackOptions = (racksData?.listRacks?.rows ?? []).map((r) => ({
    value: r.id,
    label: r.name,
  }));

  const warehouseOptions = (warehousesData?.listWarehouses?.rows ?? []).map((w) => ({
    value: w.id,
    label: w.name,
  }));

  function openCreate() {
    setEditing(null);
    setForm(defaultForm);
    setModalOpen(true);
  }

  function openEdit(row: InventoryTransferRow) {
    setEditing(row);
    setForm({
      productId: row.productId,
      fromType: row.fromType,
      fromRackId: row.fromRackId ?? '',
      toType: row.toType,
      toRackId: row.toRackId ?? '',
      warehouseId: row.warehouseId ?? '',
      quantity: row.quantity,
      lossPercent: Number(row.lossPercent) || 0,
      lossKg: Number(row.lossKg) || 0,
      notes: row.notes ?? '',
    });
    setModalOpen(true);
  }

  function transferPayload() {
    return {
      ...(editing ? { id: editing.id } : {}),
      productId: form.productId,
      fromType: form.fromType,
      fromRackId: form.fromType === 'RACK' ? form.fromRackId || undefined : undefined,
      toType: form.toType,
      toRackId: form.toType === 'RACK' ? form.toRackId || undefined : undefined,
      warehouseId: form.warehouseId || undefined,
      quantity: form.quantity,
      lossPercent: form.lossPercent || 0,
      lossKg: form.lossKg || 0,
      notes: form.notes || undefined,
    };
  }

  async function onSaveDraft() {
    await save({ ...transferPayload(), status: 'DRAFT' }).unwrap();
    setModalOpen(false);
  }

  async function onSaveAndConfirm() {
    const result = await save(transferPayload()).unwrap();
    if (result.addUpdateInventoryTransfer?.id) {
      await confirm(result.addUpdateInventoryTransfer.id).unwrap();
    }
    setModalOpen(false);
  }

  const formLoss = resolveLossKg(form.quantity, form.lossPercent, form.lossKg);
  const formSourceNeed = form.quantity + formLoss;

  async function onConfirmExisting() {
    if (!editing) return;
    await confirm(editing.id).unwrap();
    setModalOpen(false);
  }

  const isFormValid = !!form.productId && form.quantity > 0;

  function statusColor(status: string) {
    if (status === 'CONFIRMED') return 'green';
    return 'orange';
  }

  return (
    <Stack gap="md">
      <PageHeader
        title={t('inventory.title')}
        description={t('inventory.description')}
      />
      {isError ? <Alert color="red" title={t('inventory.loadFailed')} /> : null}
      <ResourceLayout
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        onQueryChange={setQuery}
        getRowId={(row) => row.id}
        emptyTitle={t('inventory.emptyTitle')}
        emptyDescription={t('inventory.emptyDescription')}
        onRowClick={canOpenRow ? openEdit : undefined}
        mobileView="list"
        addButton={
          access.canCreate ? (
            <Button onClick={openCreate}>{t('inventory.newTransfer')}</Button>
          ) : undefined
        }
        columns={[
          {
            key: 'product',
            header: t('common.products'),
            render: (row) => row.product?.name ?? row.productId,
          },
          {
            key: 'fromType',
            header: t('inventory.from'),
            render: (row) =>
              row.fromType === 'RACK' && row.fromRack
                ? `${t('inventory.rack')}: ${row.fromRack.name}`
                : t('inventory.purchasePool'),
          },
          {
            key: 'toType',
            header: t('inventory.to'),
            render: (row) =>
              row.toType === 'RACK' && row.toRack
                ? `${t('inventory.rack')}: ${row.toRack.name}`
                : t('inventory.purchasePool'),
          },
          {
            key: 'quantity',
            header: t('orders.quantity'),
            render: (row) => {
              const loss = resolveLossKg(
                Number(row.quantity) || 0,
                Number(row.lossPercent) || 0,
                Number(row.lossKg) || 0,
              );
              return loss > 0
                ? `${row.quantity} (−${loss} ${t('inventory.loss')})`
                : String(row.quantity);
            },
          },
          {
            key: 'status',
            header: t('common.status'),
            render: (row) => (
              <Badge color={statusColor(row.status)}>{row.status}</Badge>
            ),
          },
          {
            key: 'createdAt',
            header: t('common.createdOn'),
            render: (row) =>
              row.createdAt ? new Date(row.createdAt).toLocaleDateString() : t('common.emptyValue'),
          },
        ]}
        renderCard={(row: InventoryTransferRow) => (
          <Paper p="md" withBorder radius="md">
            <Stack gap={4}>
              <Group justify="space-between">
                <Text fw={600}>{row.product?.name ?? row.productId}</Text>
                <Badge color={statusColor(row.status)}>{row.status}</Badge>
              </Group>
              <Text size="sm" c="dimmed">
                {row.fromType === 'RACK' && row.fromRack
                  ? `${t('inventory.rack')}: ${row.fromRack.name}`
                  : t('inventory.purchasePool')}
                {' → '}
                {row.toType === 'RACK' && row.toRack
                  ? `${t('inventory.rack')}: ${row.toRack.name}`
                  : t('inventory.purchasePool')}
              </Text>
              <Text size="sm">
                {t('inventory.qty')}: {row.quantity}
                {(() => {
                  const loss = resolveLossKg(
                    Number(row.quantity) || 0,
                    Number(row.lossPercent) || 0,
                    Number(row.lossKg) || 0,
                  );
                  return loss > 0 ? ` · ${t('inventory.loss')}: ${loss}` : '';
                })()}
              </Text>
            </Stack>
          </Paper>
        )}
      />

      <BottomDrawer
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t('inventory.editTransfer') : t('inventory.newTransfer')}
      >
        <Stack gap="md">
          <FormField label={t('common.products')} required>
            <Select
              searchable
              placeholder={t('inventory.selectProduct')}
              value={form.productId || null}
              onChange={(v) => setForm({ ...form, productId: v ?? '' })}
              data={productOptions}
            />
          </FormField>

          <FormField label={t('inventory.fromType')} required>
            <Select
              value={form.fromType}
              onChange={(v) =>
                setForm({ ...form, fromType: (v ?? 'PURCHASE_POOL') as typeof form.fromType })
              }
              data={FROM_TYPE_OPTIONS}
            />
          </FormField>

          {form.fromType === 'RACK' ? (
            <FormField label={t('inventory.fromRack')}>
              <Select
                searchable
                clearable
                placeholder={t('orders.selectRack')}
                value={form.fromRackId || null}
                onChange={(v) => setForm({ ...form, fromRackId: v ?? '' })}
                data={rackOptions}
              />
            </FormField>
          ) : null}

          <FormField label={t('inventory.toType')} required>
            <Select
              value={form.toType}
              onChange={(v) =>
                setForm({ ...form, toType: (v ?? 'RACK') as typeof form.toType })
              }
              data={TO_TYPE_OPTIONS}
            />
          </FormField>

          {form.toType === 'RACK' ? (
            <FormField label={t('inventory.toRack')}>
              <Select
                searchable
                clearable
                placeholder={t('orders.selectRack')}
                value={form.toRackId || null}
                onChange={(v) => setForm({ ...form, toRackId: v ?? '' })}
                data={rackOptions}
              />
            </FormField>
          ) : null}

          <FormField label={t('orders.warehouse')}>
            <Select
              searchable
              clearable
              placeholder={t('orders.searchWarehouse')}
              value={form.warehouseId || null}
              onChange={(v) => setForm({ ...form, warehouseId: v ?? '' })}
              data={warehouseOptions}
            />
          </FormField>

          <FormField label={t('inventory.moveQty')} required>
            <NumberInput
              min={0}
              decimalScale={3}
              value={form.quantity}
              onChange={(v) => setForm({ ...form, quantity: Number(v) || 0 })}
            />
          </FormField>

          <Group gap="xs" grow>
            <FormField label={t('orders.lossPercent')}>
              <NumberInput
                min={0}
                max={100}
                decimalScale={2}
                value={form.lossPercent}
                onChange={(v) => setForm({ ...form, lossPercent: Number(v) || 0 })}
              />
            </FormField>
            <FormField label={t('orders.lossKg')}>
              <NumberInput
                min={0}
                decimalScale={3}
                value={form.lossKg}
                onChange={(v) => setForm({ ...form, lossKg: Number(v) || 0 })}
              />
            </FormField>
          </Group>

          {form.quantity > 0 ? (
            <Text size="xs" c="dimmed">
              {t('inventory.transferBreakdown', {
                move: form.quantity,
                loss: formLoss,
                source: formSourceNeed,
              })}
            </Text>
          ) : null}

          <FormField label={t('common.remarks')}>
            <TextInput
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.currentTarget.value })}
              placeholder={t('inventory.notesPlaceholder')}
            />
          </FormField>

          <Group justify="space-between">
            <Group gap="xs">
              {editing && editing.status === 'DRAFT' && access.canConfirm ? (
                <Button
                  variant="light"
                  loading={confirming}
                  onClick={() => void onConfirmExisting()}
                >
                  {t('inventory.confirm')}
                </Button>
              ) : null}
              {editing && access.canDelete ? (
                <Button
                  color="red"
                  variant="light"
                  onClick={async () => {
                    await remove(editing.id);
                    setModalOpen(false);
                  }}
                >
                  {t('common.delete')}
                </Button>
              ) : null}
              {!editing || (!(editing.status === 'DRAFT' && access.canConfirm) && !access.canDelete) ? (
                <span />
              ) : null}
            </Group>
            <Group gap="xs">
              {(editing ? access.canUpdate : access.canCreate) ? (
                <Button
                  variant="default"
                  loading={saving}
                  disabled={!isFormValid}
                  onClick={() => void onSaveDraft()}
                >
                  {t('inventory.saveDraft')}
                </Button>
              ) : null}
              {(editing ? access.canUpdate && access.canConfirm : access.canCreate && access.canConfirm) ? (
                <Button
                  loading={saving || confirming}
                  disabled={!isFormValid}
                  onClick={() => void onSaveAndConfirm()}
                >
                  {t('inventory.saveConfirm')}
                </Button>
              ) : null}
            </Group>
          </Group>
        </Stack>
      </BottomDrawer>
    </Stack>
  );
}
